import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, ButtonGroup, Card, InlineStack, Text } from '@shopify/polaris';
import { useLookupStore } from '../../../../context/useStore';

// Business unit selection for the analytics deep-dive tabs, kept in the ?bu= url
// param so it survives switching between tabs. null = all units (the default).
// Value is either a BusinessUnitID (number) or the string 'unassigned'.
export function useBusinessUnitFilter() {
  const location = useLocation();
  const navigate = useNavigate();
  const raw = new URLSearchParams(location.search).get('bu');
  const businessUnitId = raw === 'unassigned' ? 'unassigned' : /^\d+$/.test(raw || '') ? Number(raw) : null;

  const setBusinessUnitId = useCallback(
    (value) => {
      const params = new URLSearchParams(window.location.search);
      if (value) params.set('bu', value);
      else params.delete('bu');
      navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate]
  );

  return [businessUnitId, setBusinessUnitId];
}

// Query string suffix for the metrics endpoints; first=true when it is the only param
export function businessUnitParam(businessUnitId, first = false) {
  if (!businessUnitId) return '';
  return `${first ? '?' : '&'}businessUnitId=${businessUnitId}`;
}

export function useBusinessUnitName(businessUnitId) {
  const businessunits = useLookupStore((state) => state.businessunits);
  if (!businessUnitId) return null;
  if (businessUnitId === 'unassigned') return 'Unassigned';
  return businessunits.find((unit) => unit.BusinessUnitID === businessUnitId)?.Name || `Unit ${businessUnitId}`;
}

// Segmented picker card shown at the top of a tab. Everything (all units together)
// is the default; picking a unit narrows every card below to documents tagged with it.
export default function BusinessUnitFilter({ value, onChange, note }) {
  const businessunits = useLookupStore((state) => state.businessunits);
  const units = [...businessunits].sort((a, b) => a.SortOrder - b.SortOrder || String(a.Name).localeCompare(String(b.Name)));

  if (!units.length) return null;

  return (
    <Card padding="300">
      <InlineStack gap="300" blockAlign="center" wrap>
        <Text variant="headingSm" as="h3">
          Business unit
        </Text>
        <ButtonGroup variant="segmented">
          <Button size="slim" pressed={!value} onClick={() => onChange(null)}>
            Everything
          </Button>
          {units.map((unit) => (
            <Button key={unit.BusinessUnitID} size="slim" pressed={value === unit.BusinessUnitID} onClick={() => onChange(unit.BusinessUnitID)}>
              {unit.Name}
            </Button>
          ))}
          <Button size="slim" pressed={value === 'unassigned'} onClick={() => onChange('unassigned')}>
            Unassigned
          </Button>
        </ButtonGroup>
      </InlineStack>
      {value ? (
        <Box paddingBlockStart="200">
          <Text tone="subdued" as="p" variant="bodySm">
            {value === 'unassigned'
              ? 'Showing only documents without a business unit. Units are tagged since 2025, so everything before that lands here.'
              : 'Showing only documents tagged with this unit. Units are tagged since 2025 — earlier periods are mostly empty in this view.'}
            {note ? ` ${note}` : ''}
          </Text>
        </Box>
      ) : null}
    </Card>
  );
}
